import {
  BodyText,
  ButtonRow,
  Field,
  Heading,
} from '@grays-npm/component-library';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalProps } from '../../../components/Modal';
import { Slider } from '../../../components/Slider';
import { LoadingButton } from '../../../components/Button';
import { ApiError } from '../../../services/apiError';
import {
  formatCurrency,
  isValidCurrency,
  parseCurrency,
} from '../../../utils/helpers';
import { useCMSData } from '../../../state/content-manager';
import { ListingModalData, ListingModalTypeEnum } from './types';
import TagManager from 'react-gtm-module';

type LowerReserveModalProps = Omit<ModalProps, 'title'> &
  ListingModalData<ListingModalTypeEnum.LowerReserve> & {
    onLowerReserve: (reserve: number) => void;
    apiError?: ApiError;
  };

const Text = styled(BodyText)`
  margin: 0 0 ${({ theme }) => theme.spacings[5].rem};
  text-align: center;
`;

const OpportunitiesText = styled(Text)`
  margin-top: -${({ theme }) => theme.spacings[4].rem};
`;

const BoldText = styled.span`
  color: ${({ theme }) => theme.colors.primary.graysRed};
  font-weight: 700;
`;

const Input = styled(Field)`
  ${({ theme }) => theme.typography.headings[3]['sm-lg']}
  font-weight: 400;
  text-align: center;
`;

const Hint = styled(BodyText)`
  color: ${({ theme }) => theme.colors.system.systemBlue};
  margin-bottom: ${({ theme }) => theme.spacings[2].rem};
  text-align: center;
`;

const StyledSlider = styled(Slider)`
  margin-bottom: ${({ theme }) => theme.spacings[2].rem};
`;

const Data = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacings[7].rem};
`;

const SecondaryColumn = styled.div`
  text-align: right;
`;

export const LowerReserveModal: React.FC<LowerReserveModalProps> = ({
  bid,
  isLoading,
  isButtonLoading,
  isSuccess,
  isOpen,
  remainingActionCount,
  onLowerReserve,
  onClose,
  reserve,
  apiError,
  listingType,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const maxReserve = reserve > 1 ? reserve - 1 : reserve;
  const [newReserve, setNewReserve] = useState(formatCurrency(maxReserve));

  const cmsData = useCMSData();
  const { title, successText } = cmsData.modals.lowerReserve;

  useEffect(() => {
    if (apiError) {
      const { message, validationMessages } = apiError;
      validationMessages
        ? setErrorMessage(validationMessages[0].message)
        : setErrorMessage(message);
    }
  }, [apiError]);

  useEffect(() => {
    // Remove error message on close & reopen, or when switching modals
    setErrorMessage('');
  }, [isOpen]);

  const handleReserveChange = (value: string) => {
    const currency = !isNaN(Number(value))
      ? formatCurrency(Number(value))
      : value;

    setErrorMessage('');
    setNewReserve(currency || '$');
  };

  const handleLowerReserve = () => {
    if (!isValidCurrency(newReserve)) {
      setErrorMessage('Please enter a valid reserve');
      return;
    }

    const result = parseCurrency(newReserve);

    if (result < bid) {
      setErrorMessage('Must be greater than the latest bid');
      return;
    } else if (maxReserve < result) {
      setErrorMessage('Must be lower than the current reserve');
      return;
    }

    setNewReserve(formatCurrency(result));
    onLowerReserve(result);
  };

  return (
    <Modal
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      titleVariant="secondary"
      isSuccess={isSuccess}
      successText={successText}
    >
      {remainingActionCount > 0 && (
        <OpportunitiesText size="md">
          You have <BoldText>{remainingActionCount} opportunities</BoldText> to
          lower the reserve
        </OpportunitiesText>
      )}
      <Input
        errorMessage={errorMessage}
        iconSize="lg"
        onChange={(e) => {
          handleReserveChange(e.target.value);
          TagManager.dataLayer({
            dataLayer: {
              event: `${listingType}LowerReserveModal-bidInputOnChange`,
            },
          });
        }}
        value={newReserve}
      />
      <Hint size="sm">Slide to adjust reserve</Hint>
      <StyledSlider
        onChange={(value) => {
          handleReserveChange(value);
          TagManager.dataLayer({
            dataLayer: {
              event: `${listingType}LowerReserveModal-sliderOnChange`,
            },
          });
        }}
        max={maxReserve}
        min={bid}
        value={parseCurrency(newReserve)}
      />
      <Data>
        <div>
          <BodyText size="sm">Latest Bid:</BodyText>
          <Heading size={4}>{formatCurrency(bid)}</Heading>
        </div>
        <SecondaryColumn>
          <BodyText size="sm">Current Reserve:</BodyText>
          <Heading size={4}>{formatCurrency(reserve)}</Heading>
        </SecondaryColumn>
      </Data>
      <ButtonRow>
        <LoadingButton
          onClick={handleLowerReserve}
          onClose={onClose}
          size="large"
          fullWidth={true}
          disabled={isButtonLoading}
          isLoading={isButtonLoading}
          isSuccess={isSuccess}
        >
          Lower reserve
        </LoadingButton>
      </ButtonRow>
    </Modal>
  );
};
